import { useMemo, useState, Fragment } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { QuickDates, money, num } from "@/components/sales-reports/EnhancedReports";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, Printer, ChevronRight, ChevronDown, ArrowUpDown } from "lucide-react";

function useSuppliers() {
  const { data } = useResource("/suppliers");
  const arr = Array.isArray(data) ? data : data?.items || [];
  return arr.map((s) => ({ id: s._id || s.id, code: s.supplierId || s.code || "", name: s.name || "", gstin: s.gstin || "" }));
}

function SupplierSelect({ value, onChange, testId = "pr-supplier", placeholder = "All suppliers" }) {
  const sups = useSuppliers();
  return (
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-56 rounded-md border border-stone-200 bg-white px-2 text-xs">
      <option value="">{placeholder}</option>
      {sups.map((s) => <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ""}</option>)}
    </select>
  );
}

function ExportBar({ report, params, testPrefix }) {
  const [busy, setBusy] = useState(false);
  const run = async (fmt) => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      Object.entries({ ...params, report, format: fmt }).forEach(([k, v]) => { if (v) p.set(k, v); });
      const res = await api.get(`/purchase-reports/export?${p.toString()}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `${report}.${fmt === "excel" ? "xlsx" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported (${fmt.toUpperCase()})`);
    } catch (err) { toast.error(apiErrorMessage(err, "Export failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-1.5">
      <Button data-testid={`${testPrefix}-excel`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("excel")}><Download className="h-3.5 w-3.5" /> Excel</Button>
      <Button data-testid={`${testPrefix}-csv`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("csv")}><Download className="h-3.5 w-3.5" /> CSV</Button>
      <Button data-testid={`${testPrefix}-pdf`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("pdf")}><Download className="h-3.5 w-3.5" /> PDF</Button>
      <Button data-testid={`${testPrefix}-print`} variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => window.print()}><Printer className="h-3.5 w-3.5" /> Print</Button>
    </div>
  );
}

function KpiRow({ items }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
      {items.map(([label, val, tid]) => (
        <div key={label} className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm" data-testid={tid}>
          <div className="font-display text-lg font-bold text-stone-900">{val}</div>
          <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
        </div>
      ))}
    </div>
  );
}

function useSort(defaultKey, defaultDir = "desc") {
  const [key, setKey] = useState(defaultKey);
  const [dir, setDir] = useState(defaultDir);
  const toggle = (k) => { if (k === key) setDir(dir === "asc" ? "desc" : "asc"); else { setKey(k); setDir("desc"); } };
  const sort = (rows) => [...(rows || [])].sort((a, b) => {
    const av = a[key], bv = b[key];
    const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av ?? "").localeCompare(String(bv ?? ""));
    return dir === "asc" ? cmp : -cmp;
  });
  return { key, dir, toggle, sort };
}

function Th({ col, label, right, sort }) {
  return (
    <th className={`px-3 py-2.5 ${right ? "text-right" : ""} cursor-pointer select-none`} onClick={() => sort.toggle(col)}>
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className="h-3 w-3 opacity-40" /></span>
    </th>
  );
}

function FilterCard({ children }) {
  return <div className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">{children}</div>;
}

/* ================= A. Supplier Purchase Summary ================= */
function SupplierSummary() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [supplierId, setSupplierId] = useState(""); const [gstin, setGstin] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", supplierId: "", gstin: "" });
  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, supplierId, gstin });
  const qs = useMemo(() => { const p = new URLSearchParams(); Object.entries(applied).forEach(([k, v]) => v && p.set(k, v)); const s = p.toString(); return s ? `?${s}` : ""; }, [applied]);
  const { data, isLoading } = useResource(`/purchase-reports/summary${qs}`);
  const rep = data || {}; const t = rep.totals || {};
  const sort = useSort("total");
  const rows = sort.sort(rep.rows);
  return (
    <div className="space-y-4">
      <FilterCard>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div><Label className="text-[10px]">Supplier</Label><SupplierSelect value={supplierId} onChange={setSupplierId} /></div>
        <div><Label className="text-[10px]">GSTIN</Label><Input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="GSTIN" className="h-8 w-40 text-xs" data-testid="ps-gstin" /></div>
        <ExportBar report="summary" params={applied} testPrefix="ps-export" />
      </FilterCard>
      {isLoading ? <LoadingState label="Loading supplier summary…" /> : (
        <>
          <KpiRow items={[
            ["Suppliers", num(t.suppliers), "ps-kpi-suppliers"], ["Bills", num(t.bills), "ps-kpi-bills"],
            ["Qty", num(t.qty), "ps-kpi-qty"], ["Taxable", money(t.taxable), "ps-kpi-taxable"],
            ["GST", money(t.gst), "ps-kpi-gst"], ["Total Purchase", money(t.total), "ps-kpi-total"],
            ["Paid", money(t.paid), "ps-kpi-paid"], ["Outstanding", money(t.outstanding), "ps-kpi-outstanding"],
          ]} />
          {!rows || rows.length === 0 ? <EmptyState title="No purchases" message="No confirmed purchase bills for the selected filters." /> : (
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="ps-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                  <tr>
                    <Th col="supplierName" label="Supplier" sort={sort} />
                    <th className="px-3 py-2.5">Code</th><th className="px-3 py-2.5">GSTIN</th><th className="px-3 py-2.5">Parent</th>
                    <Th col="bills" label="Bills" right sort={sort} /><Th col="qty" label="Qty" right sort={sort} />
                    <Th col="taxable" label="Taxable" right sort={sort} /><Th col="gst" label="GST" right sort={sort} />
                    <Th col="total" label="Total" right sort={sort} /><Th col="paid" label="Paid" right sort={sort} />
                    <Th col="outstanding" label="Outstanding" right sort={sort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-stone-50/60" data-testid={`ps-row-${i}`}>
                      <td className="px-3 py-2.5 text-stone-800">{r.supplierName}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.supplierCode || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.gstin || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-500">{r.parentSupplier || "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.bills}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{num(r.qty)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.taxable)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.gst)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{money(r.total)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.paid)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ================= B. Item-Wise Purchase ================= */
function ItemWisePurchase() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [supplierId, setSupplierId] = useState(""); const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", supplierId: "", category: "", search: "" });
  const [open, setOpen] = useState({});
  const [openInv, setOpenInv] = useState({});
  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, supplierId, category, search });
  const qs = useMemo(() => { const p = new URLSearchParams(); Object.entries(applied).forEach(([k, v]) => v && p.set(k, v)); const s = p.toString(); return s ? `?${s}` : ""; }, [applied]);
  const { data, isLoading } = useResource(`/purchase-reports/item-wise${qs}`);
  const { data: ingsRaw } = useResource("/ingredients");
  const categories = useMemo(() => {
    const arr = Array.isArray(ingsRaw) ? ingsRaw : ingsRaw?.items || [];
    return [...new Set(arr.map((i) => i.category).filter(Boolean))].sort();
  }, [ingsRaw]);
  const rep = data || {}; const t = rep.totals || {};
  const sort = useSort("taxable");
  const rows = sort.sort(rep.rows);
  return (
    <div className="space-y-4">
      <FilterCard>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div><Label className="text-[10px]">Supplier</Label><SupplierSelect value={supplierId} onChange={setSupplierId} /></div>
        <div><Label className="text-[10px]">Category</Label>
          <select data-testid="piw-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-8 w-36 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="">All</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><Label className="text-[10px]">Search item</Label><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="name / code" className="h-8 w-44 text-xs" data-testid="piw-search" /></div>
        <ExportBar report="itemwise" params={applied} testPrefix="piw-export" />
      </FilterCard>
      {isLoading ? <LoadingState label="Loading item-wise purchases…" /> : (
        <>
          <KpiRow items={[
            ["Items", num(t.items), "piw-kpi-items"], ["Qty", num(t.qty), "piw-kpi-qty"],
            ["Taxable", money(t.taxable), "piw-kpi-taxable"], ["Total Value", money(t.value), "piw-kpi-value"],
            ["Avg / Unit", t.qty ? money(t.taxable / t.qty) : "—", "piw-kpi-avg"],
          ]} />
          <p className="text-xs text-stone-500">Average Purchase Price = Total taxable purchase value ÷ Total quantity purchased (from actual bills, not master price). Click a row to see every distinct price paid.</p>
          {!rows || rows.length === 0 ? <EmptyState title="No items" message="No purchased items for the selected filters." /> : (
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="piw-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                  <tr>
                    <th className="px-3 py-2.5 w-6"></th>
                    <Th col="itemName" label="Item" sort={sort} /><th className="px-3 py-2.5">Code</th><th className="px-3 py-2.5">Unit</th>
                    <Th col="qty" label="Qty" right sort={sort} /><Th col="bills" label="Bills" right sort={sort} />
                    <Th col="suppliers" label="Suppliers" right sort={sort} /><Th col="taxable" label="Taxable" right sort={sort} />
                    <th className="px-3 py-2.5 text-right">Low / High / Latest</th>
                    <Th col="avgPrice" label="Avg Price" right sort={sort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r, i) => (
                    <Fragment key={i}>
                      <tr className="cursor-pointer hover:bg-stone-50/60" data-testid={`piw-row-${i}`} onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}>
                        <td className="px-3 py-2.5 text-stone-400">{open[i] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                        <td className="px-3 py-2.5 text-stone-800">{r.itemName}
                          {r.priceVaries && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`piw-varies-${i}`}>PRICE VARIES</span>}
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs">{r.itemCode || "—"}</td>
                        <td className="px-3 py-2.5">{r.unit || "—"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{num(r.qty)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.bills}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.suppliers}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{money(r.taxable)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-xs text-stone-500">{money(r.lowestPrice)} / {money(r.highestPrice)} / {money(r.latestPrice)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{money(r.avgPrice)}</td>
                      </tr>
                      {open[i] && (
                        <tr><td colSpan={10} className="bg-stone-50/60 px-3 py-2" data-testid={`piw-prices-${i}`}>
                          <table className="w-full text-xs">
                            <thead className="text-left text-[10px] uppercase text-stone-400">
                              <tr><th className="px-3 py-1.5 w-6"></th><th className="px-3 py-1.5">Purchase Price</th><th className="px-3 py-1.5 text-right">Qty</th><th className="px-3 py-1.5">Unit</th><th className="px-3 py-1.5 text-right">Value</th><th className="px-3 py-1.5 text-right">#Bills</th></tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                              {r.prices.map((pr, pri) => {
                                const k = `${i}-${pri}`;
                                return (
                                  <Fragment key={k}>
                                    <tr className="cursor-pointer hover:bg-amber-50/40" data-testid={`piw-price-${k}`} onClick={() => setOpenInv((o) => ({ ...o, [k]: !o[k] }))}>
                                      <td className="px-3 py-1.5 text-stone-400">{openInv[k] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                                      <td className="px-3 py-1.5 font-semibold text-stone-800">{money(pr.price)}</td>
                                      <td className="px-3 py-1.5 text-right tabular-nums">{num(pr.qty)}</td>
                                      <td className="px-3 py-1.5">{r.unit}</td>
                                      <td className="px-3 py-1.5 text-right tabular-nums">{money(pr.value)}</td>
                                      <td className="px-3 py-1.5 text-right tabular-nums">{pr.bills}</td>
                                    </tr>
                                    {openInv[k] && (
                                      <tr><td colSpan={6} className="bg-white px-3 py-2">
                                        <table className="w-full text-xs" data-testid={`piw-inv-${k}`}>
                                          <thead className="text-left text-[10px] uppercase text-stone-400"><tr><th className="px-2 py-1">Invoice</th><th className="px-2 py-1">Date</th><th className="px-2 py-1">Supplier</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Value</th></tr></thead>
                                          <tbody className="divide-y divide-stone-100">
                                            {pr.invoices.map((iv, ii) => (
                                              <tr key={ii}><td className="px-2 py-1 font-mono">{iv.invoiceNo}</td><td className="px-2 py-1">{iv.invoiceDate}</td><td className="px-2 py-1">{iv.supplier}</td><td className="px-2 py-1 text-right tabular-nums">{num(iv.qty)}</td><td className="px-2 py-1 text-right tabular-nums">{money(iv.rate)}</td><td className="px-2 py-1 text-right tabular-nums">{money(iv.value)}</td></tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </td></tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </td></tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ================= C. Supplier Detailed Purchase ================= */
function SupplierDetailed() {
  const [supplierId, setSupplierId] = useState("");
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [applied, setApplied] = useState(null);
  const [open, setOpen] = useState({});
  const apply = (f = from, t = to) => { if (supplierId) setApplied({ supplierId, dateFrom: f, dateTo: t }); };
  const qs = useMemo(() => { if (!applied) return null; const p = new URLSearchParams(); Object.entries(applied).forEach(([k, v]) => v && p.set(k, v)); return `?${p.toString()}`; }, [applied]);
  const { data, isLoading } = useResource(applied ? `/purchase-reports/detailed${qs}` : null, { enabled: !!applied });
  const rep = data || {}; const rec = rep.reconciliation || {}; const sup = rep.supplier || {};
  return (
    <div className="space-y-4">
      <FilterCard>
        <div><Label className="text-[10px]">Supplier (required)</Label><SupplierSelect value={supplierId} onChange={(v) => { setSupplierId(v); setApplied(null); }} testId="pd-supplier" placeholder="Select supplier…" /></div>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <Button size="sm" className="h-8 bg-amber-700 hover:bg-amber-800 text-xs" disabled={!supplierId} onClick={() => apply()} data-testid="pd-run">Run Report</Button>
        {applied && <ExportBar report="detailed" params={applied} testPrefix="pd-export" />}
      </FilterCard>
      {!applied ? <EmptyState title="Select a supplier" message="Choose a supplier and date range, then Run Report." /> :
        isLoading ? <LoadingState label="Loading supplier detail…" /> : (
          <>
            <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="pd-header">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-display text-lg font-bold text-stone-900">{sup.supplierName}</div>
                  <div className="text-xs text-stone-500">{sup.supplierCode} · GSTIN {sup.gstin || "—"} · Parent {sup.parentSupplier || "—"}</div>
                </div>
                <div className="text-right text-xs text-stone-600">
                  <div>Period: {rep.dateFrom || "beginning"} → {rep.dateTo || "date"}</div>
                  <div>{rec.billCount} bill{rec.billCount !== 1 ? "s" : ""} · {rec.productCount} product{rec.productCount !== 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>
            {(rep.rows || []).length === 0 ? <EmptyState title="No purchases" message="No confirmed purchases from this supplier in range." /> : (
              <div className="space-y-3">
                {rep.rows.map((p, pi) => (
                  <div key={pi} className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid={`pd-product-${pi}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-stone-50 px-4 py-2.5">
                      <div>
                        <span className="font-semibold text-stone-800">{p.itemName}</span>
                        <span className="ml-2 font-mono text-[10px] text-stone-400">{p.itemCode}</span>
                        {p.priceVaries && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`pd-varies-${pi}`}>PRICE VARIES</span>}
                      </div>
                      <div className="text-xs text-stone-600">
                        Low {money(p.lowestPrice)} · High {money(p.highestPrice)} · Avg <b>{money(p.avgPrice)}</b> · Latest {money(p.latestPrice)} · Total {num(p.qty)} {p.unit} / <b>{money(p.taxable)}</b>
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="text-left text-[10px] uppercase text-stone-400">
                        <tr><th className="px-4 py-1.5">Purchase Price</th><th className="px-4 py-1.5 text-right">Qty</th><th className="px-4 py-1.5">Unit</th><th className="px-4 py-1.5 text-right">Value</th><th className="px-4 py-1.5"></th></tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {p.prices.map((pr, pri) => {
                          const k = `${pi}-${pri}`;
                          return (
                            <Fragment key={k}>
                              <tr className="cursor-pointer hover:bg-amber-50/40" onClick={() => setOpen((o) => ({ ...o, [k]: !o[k] }))} data-testid={`pd-price-${k}`}>
                                <td className="px-4 py-2 font-semibold text-stone-800">{money(pr.price)}</td>
                                <td className="px-4 py-2 text-right tabular-nums">{num(pr.qty)}</td>
                                <td className="px-4 py-2">{p.unit}</td>
                                <td className="px-4 py-2 text-right tabular-nums">{money(pr.value)}</td>
                                <td className="px-4 py-2 text-stone-400">{open[k] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                              </tr>
                              {open[k] && (
                                <tr key={`${k}-inv`}>
                                  <td colSpan={5} className="bg-stone-50/60 px-4 py-2">  <table className="w-full text-xs" data-testid={`pd-inv-${k}`}>
                                      <thead className="text-left text-[10px] uppercase text-stone-400"><tr><th className="px-2 py-1">Invoice</th><th className="px-2 py-1">Date</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Value</th></tr></thead>
                                      <tbody className="divide-y divide-stone-100">
                                        {pr.invoices.map((iv, ii) => (
                                          <tr key={ii}><td className="px-2 py-1 font-mono">{iv.invoiceNo}</td><td className="px-2 py-1">{iv.invoiceDate}</td><td className="px-2 py-1 text-right tabular-nums">{num(iv.qty)}</td><td className="px-2 py-1 text-right tabular-nums">{money(iv.rate)}</td><td className="px-2 py-1 text-right tabular-nums">{money(iv.value)}</td></tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                        <tr className="bg-amber-50/40 font-semibold">
                          <td className="px-4 py-2 text-stone-800">{p.itemName} — Total</td>
                          <td className="px-4 py-2 text-right tabular-nums">{num(p.qty)}</td>
                          <td className="px-4 py-2">{p.unit}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{money(p.taxable)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm" data-testid="pd-recon">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-emerald-800">Supplier Total (Σ product purchases)</span>
                    <span className="font-display text-lg font-bold text-emerald-900">{money(rec.totalTaxable)}</span>
                  </div>
                  <div className="mt-1 text-xs text-emerald-700">GST {money(rec.totalGst)} · Invoice value {money(rec.totalValue)} · Reconciles to product detail: {rec.reconciles ? "✓ exact" : "—"}</div>
                </div>
              </div>
            )}
          </>
        )}
    </div>
  );
}

export default function PurchaseReports() {
  const [tab, setTab] = useState("summary");
  return (
    <div className="space-y-6">
      <PageHeader title="Purchase Reports" subtitle="Supplier & item purchase analysis from confirmed Purchase Bills (invoice date)" />
      <Tabs value={tab} onValueChange={setTab} className="space-y-3">
        <TabsList data-testid="purchase-reports-tabs" className="flex-wrap bg-stone-100">
          <TabsTrigger value="summary" data-testid="pr-tab-summary">Supplier Purchase Summary</TabsTrigger>
          <TabsTrigger value="itemwise" data-testid="pr-tab-itemwise">Item-Wise Purchase</TabsTrigger>
          <TabsTrigger value="detailed" data-testid="pr-tab-detailed">Supplier Detailed Purchase</TabsTrigger>
        </TabsList>
        <TabsContent value="summary"><SupplierSummary /></TabsContent>
        <TabsContent value="itemwise"><ItemWisePurchase /></TabsContent>
        <TabsContent value="detailed"><SupplierDetailed /></TabsContent>
      </Tabs>
      <p className="text-xs text-muted-foreground">Computed from actual confirmed Purchase Bills only. The Supplier Statement (Accounts) remains the Opening → Purchases → Payments → Closing ledger.</p>
    </div>
  );
}
